import { useCallback, useMemo, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import {
  PageSidePanelSlotsContext,
  usePageSidePanelSlot,
  usePageSidePanelSlotRef,
  type PageSidePanelSide,
} from './pageSidePanelSlots'

/**
 * Lets a layout host a page's side panels on its frame (see
 * `pageSidePanelSlots`). Wrap the frame and the page content in it.
 */
export function PageSidePanelSlotProvider({ children }: { children: ReactNode }) {
  const [elements, setElements] = useState<Record<PageSidePanelSide, HTMLElement | null>>({
    start: null,
    end: null,
  })
  const setElement = useCallback((side: PageSidePanelSide, element: HTMLElement | null) => {
    setElements((current) =>
      current[side] === element ? current : { ...current, [side]: element }
    )
  }, [])
  const value = useMemo(() => ({ elements, setElement }), [elements, setElement])
  return (
    <PageSidePanelSlotsContext.Provider value={value}>
      {children}
    </PageSidePanelSlotsContext.Provider>
  )
}

/**
 * A layout's spot for one side's panels, beside the page card. Panels that
 * share a side land in the order their portals mount, so a page that shows
 * several on one side draws them from one `PageSidePanel`.
 */
export function PageSidePanelSlotTarget({ side }: { side: PageSidePanelSide }) {
  const setElement = usePageSidePanelSlotRef(side)
  return <div ref={setElement} className="flex h-full shrink-0" data-page-side-panels={side} />
}

/** Draws a page's side panel on the frame, or in place with no spot. */
export function PageSidePanel({
  side,
  children,
}: {
  side: PageSidePanelSide
  children: ReactNode
}) {
  const slot = usePageSidePanelSlot(side)
  return slot ? createPortal(children, slot) : <>{children}</>
}
